import { useCallback, useEffect, useRef } from 'react';

import { useCallbackRef } from './useCallbackRef';

type DebouncedCallback<T extends (...args: unknown[]) => unknown> = ((
  ...args: Parameters<T>
) => void) & {
  flush: () => void;
  cancel: () => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Required for Parameters<T> inference
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  options:
    | number
    | {
        delay: number;
        flushOnUnmount?: boolean;
        leading?: boolean;
        trailing?: boolean;
      }
) {
  const delay = typeof options === 'number' ? options : options.delay;
  const flushOnUnmount =
    typeof options === 'number' ? false : options.flushOnUnmount;
  const leading = typeof options === 'number' ? false : options.leading;
  const trailing =
    typeof options === 'number' ? true : (options.trailing ?? true);

  const handleCallback = useCallbackRef(callback);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const flushRef = useRef<() => void>(() => {});
  const cancelRef = useRef<() => void>(() => {});
  const leadingRef = useRef(leading);

  const lastCallback = Object.assign(
    useCallback(
      (...args: Parameters<T>) => {
        clearTimeout(debounceTimerRef.current);

        const resetWindow = () => {
          debounceTimerRef.current = undefined;
          leadingRef.current = true;
        };

        if (leading && leadingRef.current) {
          leadingRef.current = false;
          handleCallback(...args);
          // Clear any stale flush from previous trailing calls
          const noop = () => {};
          flushRef.current = noop;
          lastCallback.flush = noop;
          // Set timer to reset leadingRef after delay (cleared if another call comes in)
          debounceTimerRef.current = setTimeout(resetWindow, delay);
          return;
        }

        if (!trailing) {
          leadingRef.current = false;
          // Extend the cooldown window without scheduling a trailing callback.
          debounceTimerRef.current = setTimeout(resetWindow, delay);
          return;
        }

        const flush = () => {
          if (debounceTimerRef.current) {
            resetWindow();
            handleCallback(...args);
          }
        };

        flushRef.current = flush;
        lastCallback.flush = flush;
        debounceTimerRef.current = setTimeout(flush, delay);
        leadingRef.current = false;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [handleCallback, delay, leading, trailing]
    ),
    {
      flush: flushRef.current,
      cancel: cancelRef.current,
    }
  ) as DebouncedCallback<T>;

  const cancel = () => {
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = undefined;
    leadingRef.current = true;
    const noop = () => {};
    flushRef.current = noop;
    lastCallback.flush = noop;
  };

  cancelRef.current = cancel;
  lastCallback.cancel = cancel;

  useEffect(
    () => () => {
      if (flushOnUnmount) {
        lastCallback.flush();
      }
      lastCallback.cancel();
    },
    [lastCallback, flushOnUnmount]
  );

  return lastCallback;
}
